/**
 * Statistics composable
 * Provides methods for managing Economic Goal statistics data
 */
import { ref, computed } from 'vue'
import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'
import { useCloudFunctions } from './useCloudFunctions'

/**
 * Composable for managing Statistics/Economic Goal data
 * @returns {Object} Statistics methods and state
 */
export function useStatistics() {
  const db = getFirebaseDb()
  const { callFunction } = useCloudFunctions()

  // State
  const loading = ref(false)
  const updating = ref(false)
  const error = ref(null)

  // Economic goal data
  const agentEconomicGoal = ref(null)
  const agentsAnalyses = ref([])
  const agentsList = ref([])

  // Update timestamps
  const datesUpdated = ref({
    currentMonth: null,
    previousMonth: null,
  })

  // Status listener unsubscribe
  let statusUnsubscribe = null

  // ============================================
  // Economic Goal Operations
  // ============================================

  /**
   * Get economic goal data for a specific agent
   * @param {string|number} agentId - Agent ID
   * @returns {Promise<Object|null>} Economic goal data
   */
  async function getEconomicGoalByAgent(agentId) {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'Utils', 'EconomicGoal', 'Agents', String(agentId))
      const snapshot = await getDoc(docRef)

      if (snapshot.exists()) {
        agentEconomicGoal.value = snapshot.data()
        return agentEconomicGoal.value
      }

      agentEconomicGoal.value = null
      return null
    } catch (err) {
      console.error('Error fetching economic goal:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get all agents' economic goal analyses for comparison chart
   * @returns {Promise<Array>} Array of agent analyses
   */
  async function getAllAgentsAnalyses() {
    loading.value = true
    error.value = null

    try {
      const agentsRef = collection(db, 'Utils', 'EconomicGoal', 'Agents')
      const snapshot = await getDocs(agentsRef)

      const analyses = snapshot.docs
        .map((doc) => ({
          name: doc.data().name,
          analys: doc.data().analys || 0,
          id: doc.data().id,
        }))
        .filter((agent) => !['אורן אבני', 'בדיקה'].includes(agent.name))

      agentsAnalyses.value = analyses
      return analyses
    } catch (err) {
      console.error('Error fetching agents analyses:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get list of agents (for admin dropdown)
   * @returns {Promise<Array>} Array of agents
   */
  async function getAgentsList() {
    try {
      const agentsRef = collection(db, 'Utils', 'EconomicGoal', 'Agents')
      const snapshot = await getDocs(agentsRef)

      const agents = snapshot.docs
        .map((doc) => ({
          name: doc.data().name,
          id: doc.data().id,
        }))
        .filter((agent) => !['אורן אבני', 'בדיקה'].includes(agent.name))

      agentsList.value = agents
      return agents
    } catch (err) {
      console.error('Error fetching agents list:', err)
      throw err
    }
  }

  /**
   * Get last update timestamps
   * @returns {Promise<Object>} Update timestamps
   */
  async function getUpdateTimestamps() {
    try {
      const docRef = doc(db, 'Utils', 'EconomicGoal')
      const snapshot = await getDoc(docRef)

      if (!snapshot.exists()) {
        return datesUpdated.value
      }

      const data = snapshot.data()
      datesUpdated.value = {
        previousMonth: data.previous_month_updatedAt
          ? new Date(data.previous_month_updatedAt.seconds * 1000)
          : null,
        currentMonth: data.updatedAt ? new Date(data.updatedAt.seconds * 1000) : null,
      }

      return datesUpdated.value
    } catch (err) {
      console.error('Error fetching update timestamps:', err)
      throw err
    }
  }

  // ============================================
  // Update Economic Goal Data
  // ============================================

  /**
   * Trigger economic goal data update (admin only)
   * Returns immediately - use listenToUpdateStatus for progress
   * @returns {Promise<Object>} Result
   */
  async function triggerEconomicGoalUpdate() {
    updating.value = true
    error.value = null

    try {
      const result = await callFunction('goalsUpdateEconomicGoal', {})
      return result
    } catch (err) {
      console.error('Error triggering update:', err)
      error.value = err.message
      throw err
    }
  }

  /**
   * Listen to update status changes
   * @param {Function} onStatusChange - Callback for status changes
   * @returns {Function} Unsubscribe function
   */
  function listenToUpdateStatus(onStatusChange) {
    const docRef = doc(db, 'Utils', 'EconomicGoal')

    statusUnsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) return

      const { status, errors } = snapshot.data()

      if (status === 'completed') {
        updating.value = false
        onStatusChange({
          type: 'success',
          title: 'הצלחה',
          message: 'טעינת נתוני יעד כלכלי בוצע בהצלחה',
        })
        // Refresh data
        getAllAgentsAnalyses()
        getUpdateTimestamps()
      } else if (status === 'completed_with_errors') {
        updating.value = false
        onStatusChange({
          type: 'warning',
          title: 'השלמה עם שגיאות',
          message: `התהליך הושלם אך התרחשו שגיאות: ${errors?.join(', ')}`,
        })
        getAllAgentsAnalyses()
        getUpdateTimestamps()
      } else if (status === 'error') {
        updating.value = false
        onStatusChange({
          type: 'error',
          title: 'שגיאה',
          message: 'אירעה שגיאה בתהליך העדכון',
        })
      }
    })

    return () => {
      if (statusUnsubscribe) {
        statusUnsubscribe()
        statusUnsubscribe = null
      }
    }
  }

  /**
   * Get agent's monthly archive data for a full year
   * @param {string|number} agentId - Agent ID
   * @param {number} year - Year (e.g. 2026)
   * @returns {Promise<Array>} 12-element array, one per month (null if no data)
   */
  async function getAgentYearlyArchive(agentId, year) {
    try {
      // Archive keys are offset +1 month from actual data:
      // key "2026-02" holds January data, "2026-03" holds February, etc.
      // So for display month i (0=Jan), we read key month i+2 of the same year,
      // except December (i=11) which reads from key "01" of the next year.
      const archiveKeys = Array.from({ length: 12 }, (_, i) => {
        const keyMonth = i + 2 // Jan(0)->02, Feb(1)->03, ... Nov(10)->12
        if (keyMonth <= 12) {
          return `${year}-${String(keyMonth).padStart(2, '0')}`
        }
        // December -> next year's 01
        return `${year + 1}-01`
      })

      const results = await Promise.all(
        archiveKeys.map(async (archiveKey) => {
          const docRef = doc(
            db,
            'Utils',
            'EconomicGoal',
            'MonthlyArchive',
            archiveKey,
            'Agents',
            String(agentId)
          )
          const snapshot = await getDoc(docRef)
          if (!snapshot.exists()) return null
          const d = snapshot.data()
          return {
            archiveKey,
            analys: d.analys ?? 0,
            amount: d.amount ?? 0,
            goal: d.goal ?? 0,
            name: d.name ?? '',
          }
        })
      )

      return results
    } catch (err) {
      console.error('Error fetching yearly archive:', err)
      throw err
    }
  }

  /**
   * Clear selected agent data
   */
  function clearAgentData() {
    agentEconomicGoal.value = null
  }

  /**
   * Cleanup listeners
   */
  function cleanup() {
    if (statusUnsubscribe) {
      statusUnsubscribe()
      statusUnsubscribe = null
    }
  }

  return {
    // State
    loading,
    updating,
    error,
    agentEconomicGoal,
    agentsAnalyses,
    agentsList,
    datesUpdated,

    // Methods
    getEconomicGoalByAgent,
    getAllAgentsAnalyses,
    getAgentsList,
    getAgentYearlyArchive,
    getUpdateTimestamps,
    triggerEconomicGoalUpdate,
    listenToUpdateStatus,
    clearAgentData,
    cleanup,
  }
}
